/**
 * WalletModal — proprietary wallet selection, portal-rendered.
 * No WalletConnect. No RainbowKit. Direct injected provider detection only.
 */
import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { X, ExternalLink, AlertCircle, ChevronRight } from 'lucide-react'
import { Spinner } from '@/components/ui/Spinner'
import { useModalStore } from '@/stores/modalStore'
import { useWalletStore } from '@/stores/walletStore'
import { toast } from '@/stores/toastStore'
import { detectAllProviders } from '@/lib/wallet/providers'
import type { WalletProvider, ProviderId } from '@/lib/wallet/providers'

// ─── Wallet row ──────────────────────────────────────────────────────────────

interface WalletRowProps {
  provider: WalletProvider
  connecting: ProviderId | null
  onConnect: (p: WalletProvider) => void
}

function WalletRow({ provider, connecting, onConnect }: WalletRowProps) {
  const isConnecting = connecting === provider.id
  const isBusy = !!connecting && !isConnecting
  const clickable = provider.isDetected && !isBusy

  return (
    <div
      role="button"
      tabIndex={clickable ? 0 : -1}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && clickable) onConnect(provider)
      }}
      onClick={() => clickable && onConnect(provider)}
      className={[
        'relative flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all duration-150 select-none group',
        clickable
          ? 'border-[#1E1E1E] hover:border-[#E8850C]/50 hover:bg-[#E8850C]/[0.04] cursor-pointer'
          : 'border-[#161616] opacity-40 cursor-default',
      ].join(' ')}
    >
      {/* Icon */}
      <span className="text-[22px] leading-none shrink-0">{provider.icon}</span>

      {/* Name + status */}
      <div className="flex-1 min-w-0">
        <span className="text-[13px] font-medium text-[#F5F5F5] leading-tight">
          {provider.name}
        </span>
      </div>

      {/* Right side: status indicator */}
      {isConnecting ? (
        <Spinner size="sm" />
      ) : provider.isDetected ? (
        <div className="flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 bg-[#22C55E] rounded-full" />
          <ChevronRight size={14} className="text-[#444] group-hover:text-[#E8850C] transition-colors" />
        </div>
      ) : provider.installUrl ? (
        <a
          href={provider.installUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="flex items-center gap-1 text-[11px] text-[#E8850C] hover:underline shrink-0"
        >
          Install <ExternalLink size={10} />
        </a>
      ) : null}
    </div>
  )
}

// ─── Modal ────────────────────────────────────────────────────────────────────

export function WalletModal() {
  const { isOpen, type, close } = useModalStore()
  const connect = useWalletStore((s) => s.connect)
  const [connecting, setConnecting] = useState<ProviderId | null>(null)
  const [error, setError] = useState<string | null>(null)

  const visible = isOpen && type === 'wallet'

  // Reset state when modal closes
  useEffect(() => {
    if (!visible) {
      setConnecting(null)
      setError(null)
    }
  }, [visible])

  // Close on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    if (visible) document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [visible, close])

  const allProviders = detectAllProviders()
  // Hide generic-evm if not detected (no install link makes it confusing)
  const evmProviders = allProviders.filter(
    (p) => p.type === 'evm' && (p.isDetected || (p.installUrl && p.id !== 'generic-evm'))
  )
  const stellarProviders = allProviders.filter((p) => p.type === 'stellar')

  const handleConnect = async (provider: WalletProvider) => {
    setError(null)
    setConnecting(provider.id)
    try {
      await connect(provider.id)
      toast.success(`${provider.name} connected`)
      close()
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Connection failed'
      setError(msg)
      toast.error('Connection failed', msg)
    } finally {
      setConnecting(null)
    }
  }

  const modal = (
    <AnimatePresence>
      {visible && (
        <>
          {/* Backdrop */}
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm"
            onClick={close}
          />

          {/* Panel */}
          <motion.div
            key="panel"
            initial={{ opacity: 0, scale: 0.96, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 10 }}
            transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
            className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-[340px] px-4"
          >
            <div className="bg-[#141414] border border-[#1E1E1E] rounded-2xl shadow-[0_0_80px_rgba(232,133,12,0.08),0_0_100px_rgba(0,0,0,0.9)] overflow-hidden">
              {/* Header with brand */}
              <div className="flex items-center justify-between px-4 pt-4 pb-3">
                <div className="flex items-center gap-2.5">
                  <div className="w-7 h-7 rounded-lg bg-[#E8850C] flex items-center justify-center shadow-[0_0_12px_rgba(232,133,12,0.4)]">
                    <span className="text-white text-[10px] font-bold tracking-tight">S6</span>
                  </div>
                  <div>
                    <h2 className="text-[13px] font-semibold text-[#F5F5F5] leading-tight">Connect Wallet</h2>
                    <p className="text-[10px] text-[#555] mt-0.5">SaaSFarm</p>
                  </div>
                </div>
                <button
                  onClick={close}
                  className="w-6 h-6 flex items-center justify-center rounded-md text-[#555] hover:text-[#F5F5F5] hover:bg-[#1A1A1A] transition-colors"
                  aria-label="Close"
                >
                  <X size={14} />
                </button>
              </div>

              {/* Error banner */}
              {error && (
                <div className="mx-4 mb-3 flex items-start gap-2 p-2.5 bg-[rgba(239,68,68,0.08)] border border-[rgba(239,68,68,0.18)] rounded-lg">
                  <AlertCircle size={13} className="text-[#EF4444] shrink-0 mt-px" />
                  <p className="text-[11px] text-[#EF4444] leading-snug">{error}</p>
                </div>
              )}

              {/* Wallet list */}
              <div className="px-4 pb-3 flex flex-col gap-1.5">
                {evmProviders.length > 0 && (
                  <>
                    <p className="text-[9px] font-semibold text-[#444] uppercase tracking-[0.12em] px-1 pt-1 pb-0.5">EVM</p>
                    {evmProviders.map((p) => (
                      <WalletRow key={p.id} provider={p} connecting={connecting} onConnect={handleConnect} />
                    ))}
                  </>
                )}
                {stellarProviders.length > 0 && (
                  <>
                    <p className="text-[9px] font-semibold text-[#444] uppercase tracking-[0.12em] px-1 pt-2 pb-0.5">Stellar</p>
                    {stellarProviders.map((p) => (
                      <WalletRow key={p.id} provider={p} connecting={connecting} onConnect={handleConnect} />
                    ))}
                  </>
                )}
              </div>

              {/* Footer */}
              <div className="border-t border-[#1A1A1A] px-4 py-2.5">
                <p className="text-[9px] text-[#444] text-center">
                  By connecting you agree to our{' '}
                  <a href="/terms" className="text-[#E8850C] hover:underline">
                    Terms of Service
                  </a>
                </p>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )

  return createPortal(modal, document.body)
}
